(() => {
  const JSONP_TIMEOUT_MS = 5000;
  const COOKIE_NAME = 'c_se_cid';
  const COOKIE_MAX_AGE_SECONDS = 63072000;
  const JSONP_PATH = '/pycre5/jsonp/';
  const DESKTOP_MEDIA_QUERY = '(min-width: 741px)';
  const CALLBACK_PREFIX = 'c_seCb_';
  const NOOP_CALLBACK_NAME = 'c_seNoop';
  const PRODUCT_IMAGE_WIDTH = 676;
  const SLIDER_ITEM_THRESHOLD = 4;
  const SCROLL_EDGE_TOLERANCE_PX = 1;
  const BOT_USER_AGENT_PATTERN = /bot|crawler|spider/i;
  const ALLOWED_URL_PATTERN = /^https?:\/\//i;
  const DEFAULT_MONEY_FORMAT = '¥{{amount_no_decimals_with_comma_separator}}';

  const CONFIG = Object.assign(
    {
      enabled: false,
      host: '',
      merchant: '',
      itemsFallback: false,
      moneyFormat: DEFAULT_MONEY_FORMAT,
    },
    window.SilvereggConfig || {},
  );

  let callbackSequence = 0;
  window[NOOP_CALLBACK_NAME] = () => {};

  function readCookie() {
    const cookiePrefix = `${COOKIE_NAME}=`;
    const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(cookiePrefix));
    return cookie ? decodeURIComponent(cookie.slice(cookiePrefix.length)) : '';
  }

  function readStoredCookieId() {
    try {
      return window.localStorage.getItem(COOKIE_NAME) || '';
    } catch (error) {
      return '';
    }
  }

  function writeCookie(cookieId) {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(cookieId)}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
  }

  function writeStoredCookieId(cookieId) {
    try {
      window.localStorage.setItem(COOKIE_NAME, cookieId);
    } catch (error) {
      // localStorage が利用できない場合も cookie のみで継続する。
    }
  }

  function getCookieId() {
    const cookieId = readCookie();
    if (cookieId) {
      writeStoredCookieId(cookieId);
      return cookieId;
    }

    const storedCookieId = readStoredCookieId();
    if (storedCookieId) {
      writeCookie(storedCookieId);
      return storedCookieId;
    }

    if (typeof window.crypto?.randomUUID !== 'function') {
      return '';
    }

    const newCookieId = window.crypto.randomUUID();
    writeCookie(newCookieId);
    writeStoredCookieId(newCookieId);
    return newCookieId;
  }

  function isCrawler() {
    return navigator.webdriver === true || BOT_USER_AGENT_PATTERN.test(navigator.userAgent);
  }

  function buildBaseUrl(endpoint) {
    return `https://${CONFIG.host}${JSONP_PATH}${endpoint}`;
  }

  function loadJsonp(endpoint, params) {
    return new Promise((resolve, reject) => {
      callbackSequence += 1;
      const callbackName = `${CALLBACK_PREFIX}${callbackSequence}`;
      const script = document.createElement('script');
      const search = new URLSearchParams(params);
      let timeoutId;

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script.remove();
        delete window[callbackName];
      };

      window[callbackName] = (response) => {
        cleanup();
        resolve(response);
      };

      search.set('callback', callbackName);
      script.src = `${buildBaseUrl(endpoint)}?${search.toString()}`;
      script.async = true;
      script.onerror = () => {
        cleanup();
        reject(new Error('Silver Egg JSONP request failed.'));
      };
      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Silver Egg JSONP request timed out.'));
      }, JSONP_TIMEOUT_MS);
      document.head.appendChild(script);
    });
  }

  // Silver Egg APIの価格は円単位のため、formatMoneyが扱うcentsへ変換する。
  function yenToCents(yen) {
    if ((typeof yen !== 'number' && typeof yen !== 'string') || String(yen).trim() === '') {
      return null;
    }
    const amount = Number(yen);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  // 整形済み金額文字列から円単位の数値を取得する。
  function parseMoneyString(moneyString) {
    if (typeof moneyString !== 'string') {
      return null;
    }
    const numericString = moneyString.replace(/[^\d.]/g, '');
    if (numericString === '') {
      return null;
    }
    const amount = Number(numericString);
    return Number.isFinite(amount) ? amount : null;
  }

  function formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) {
      return '';
    }

    const fixedAmount = (amount / 100).toFixed(2);
    const [integer, decimals] = fixedAmount.split('.');
    const thousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const dotThousands = thousands.replace(/,/g, '.');
    const commaThousands = thousands.replace(/,/g, ' ');
    const format = CONFIG.moneyFormat || DEFAULT_MONEY_FORMAT;

    return format
      .replace(/{{\s*amount\s*}}/g, `${thousands}.${decimals}`)
      .replace(/{{\s*amount_no_decimals\s*}}/g, thousands)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, `${dotThousands},${decimals}`)
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, dotThousands)
      .replace(/{{\s*amount_with_apostrophe_separator\s*}}/g, `${commaThousands}.${decimals}`);
  }

  // nullの金額は空表示にし、有効なcentsだけを整形する。
  function formatNullableMoney(cents) {
    return cents === null ? '' : formatMoney(cents);
  }

  function resizeImageUrl(imageUrl) {
    if (!imageUrl) {
      return '';
    }
    const separator = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${separator}width=${PRODUCT_IMAGE_WIDTH}`;
  }

  function stripImageQuery(url) {
    return String(url || '').split('?')[0];
  }

  // カードが表示するfeatured_imageと同じ画像のバリアントを優先する。
  function resolveDisplayedVariantId(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const featuredImageSrc = stripImageQuery(product?.featured_image);
    if (featuredImageSrc) {
      const matchedVariant = variants.find(
        (variant) => stripImageQuery(variant.featured_image?.src) === featuredImageSrc,
      );
      if (matchedVariant) {
        return matchedVariant.id;
      }
    }
    const availableVariant = variants.find((variant) => variant.available);
    return (availableVariant || variants[0])?.id ?? null;
  }

  async function fetchProduct(productId) {
    const handle = String(productId).toLowerCase();
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) {
        return null;
      }
      const product = await response.json();
      return { productId, handle, product };
    } catch (error) {
      return null;
    }
  }

  async function fetchProducts(productIds) {
    const requests = productIds.map((productId) => fetchProduct(productId));
    const results = await Promise.allSettled(requests);
    const productsById = new Map();
    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        productsById.set(result.value.productId, result.value);
      }
    });
    return productsById;
  }

  function getApiItem(items, index) {
    if (!Array.isArray(items)) {
      return null;
    }
    const item = items[index];
    return Array.isArray(item) ? item[0] || null : item || null;
  }

  function isAllowedUrl(url) {
    return typeof url === 'string' && ALLOWED_URL_PATTERN.test(url);
  }

  class SilvereggRecommend extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }
      this.initialized = true;
      this.initialize();
    }

    disconnectedCallback() {
      if (this.updateArrowState) {
        window.removeEventListener('resize', this.updateArrowState);
      }
    }

    getSpec() {
      return window.matchMedia(DESKTOP_MEDIA_QUERY).matches ? this.dataset.specPc : this.dataset.specSp;
    }

    getProductIds() {
      if (this.dataset.prodList) {
        try {
          const productIds = JSON.parse(this.dataset.prodList);
          return Array.isArray(productIds) ? productIds.filter(Boolean) : [];
        } catch (error) {
          return [];
        }
      }
      return this.dataset.prod ? [this.dataset.prod] : [];
    }

    buildRecommendParams(spec, cookieId) {
      const params = new URLSearchParams();
      params.set('merch', CONFIG.merchant);
      this.getProductIds().forEach((productId) => params.append('prod', productId));
      params.set('spec', spec);
      params.set('num', this.dataset.num);
      if (this.dataset.minprice) {
        params.set('minprice', this.dataset.minprice);
      }
      if (this.dataset.maxprice) {
        params.set('maxprice', this.dataset.maxprice);
      }
      if (cookieId) {
        params.set('cookie', cookieId);
      }
      return params;
    }

    async initialize() {
      if (!CONFIG.enabled || !CONFIG.host || !CONFIG.merchant || isCrawler()) {
        return;
      }

      const spec = this.getSpec();
      if (!spec) {
        return;
      }

      const cookieId = getCookieId();
      try {
        const response = await loadJsonp('recommend', this.buildRecommendParams(spec, cookieId));
        const recommendedIds = Array.isArray(response?.prod) ? response.prod : [];
        if (recommendedIds.length === 0) {
          return;
        }
        const productsById = await fetchProducts(recommendedIds);
        const recommendations = recommendedIds.map((productId, index) => ({
          productId,
          shopifyProduct: productsById.get(productId) || null,
          apiItem: getApiItem(response.items, index),
        }));
        const clickSpec = response.spec_out || spec;
        this.renderProducts(recommendations, clickSpec, cookieId, response.rqid || '');
      } catch (error) {
        // 外部サービス障害時は枠を非表示のままにする。
      }
    }

    renderProducts(recommendations, clickSpec, cookieId, requestId) {
      const template = this.querySelector('[data-silveregg-card-template]');
      const list = this.querySelector('[data-silveregg-list]');
      if (!template || !list) {
        return;
      }

      let renderedItemsCount = 0;
      recommendations.forEach(({ productId, shopifyProduct, apiItem }) => {
        const fragment = template.content.cloneNode(true);
        const card = fragment.querySelector('[data-silveregg-card]');
        const links = fragment.querySelectorAll('[data-silveregg-link]');
        const image = fragment.querySelector('[data-silveregg-image]');
        const sold = fragment.querySelector('[data-silveregg-sold]');
        const title = fragment.querySelector('[data-silveregg-title]');
        const price = fragment.querySelector('[data-silveregg-price]');
        const wishlist = fragment.querySelector('wishlist-button-collection');
        let productUrl = '';

        if (shopifyProduct) {
          productUrl = `/products/${encodeURIComponent(shopifyProduct.handle)}`;
          image.src = resizeImageUrl(shopifyProduct.product.featured_image);
          image.alt = shopifyProduct.product.title || '';
          if (shopifyProduct.product.available === false) {
            sold.hidden = false;
          } else {
            sold.remove();
          }
          title.textContent = shopifyProduct.product.title || '';
          this.renderPrice(price, shopifyProduct.product);
          wishlist.dataset.productHandle = shopifyProduct.handle;
          const variantId = resolveDisplayedVariantId(shopifyProduct.product);
          if (variantId != null) {
            wishlist.dataset.variantId = String(variantId);
          }
          card.dataset.silvereggSource = 'shopify';
        } else if (CONFIG.itemsFallback && apiItem) {
          const imageUrl = window.matchMedia(DESKTOP_MEDIA_QUERY).matches
            ? apiItem.img_url
            : apiItem.img_url_sp || apiItem.img_url;
          if (!isAllowedUrl(apiItem.ct_url) || !isAllowedUrl(imageUrl)) {
            return;
          }
          productUrl = apiItem.ct_url;
          image.src = imageUrl;
          image.alt = apiItem.title || '';
          if (apiItem.avail === false) {
            sold.hidden = false;
          } else {
            sold.remove();
          }
          title.textContent = apiItem.title || '';
          this.renderApiItemPrice(price, apiItem);
          wishlist.remove();
          card.dataset.silvereggSource = 'items';
        } else {
          return;
        }

        links.forEach((link) => {
          link.href = productUrl;
          link.addEventListener('click', (event) =>
            this.handleProductClick(event, productId, clickSpec, cookieId, requestId),
          );
        });
        card.removeAttribute('data-silveregg-card');
        list.appendChild(fragment);
        renderedItemsCount += 1;
      });

      if (renderedItemsCount === 0) {
        return;
      }
      this.hidden = false;
      this.setupGallery(renderedItemsCount);
    }

    renderPrice(priceElement, product) {
      const price = Number(product.price);
      const compareAtPrice = Number(product.compare_at_price);
      if (Number.isFinite(compareAtPrice) && compareAtPrice > price) {
        const sale = document.createElement('span');
        const compare = document.createElement('span');
        sale.className = 'c-product-card__price-sale';
        compare.className = 'c-product-card__price-compare';
        sale.textContent = formatNullableMoney(product.price);
        compare.textContent = formatNullableMoney(product.compare_at_price);
        priceElement.replaceChildren(sale, compare);
        return;
      }
      priceElement.textContent = formatNullableMoney(product.price);
    }

    renderApiItemPrice(priceElement, apiItem) {
      const numericPrice = Number(apiItem.price);
      const regularPriceYen = Number.isFinite(numericPrice) ? numericPrice : parseMoneyString(apiItem.price_string);

      if (typeof apiItem.sale_string === 'string' && apiItem.sale_string.trim() !== '') {
        const sale = document.createElement('span');
        const compare = document.createElement('span');
        sale.className = 'c-product-card__price-sale';
        compare.className = 'c-product-card__price-compare';
        sale.textContent = formatNullableMoney(yenToCents(parseMoneyString(apiItem.sale_string)));
        compare.textContent = formatNullableMoney(yenToCents(regularPriceYen));
        priceElement.replaceChildren(sale, compare);
        return;
      }
      priceElement.textContent = formatNullableMoney(yenToCents(regularPriceYen));
    }

    setupGallery(itemsCount) {
      const gallery = this.querySelector('gallery-list');
      const controls = this.querySelector('[data-silveregg-controls]');
      const progress = this.querySelector('[data-silveregg-progress]');
      if (!gallery) {
        return;
      }

      gallery.listItems = Array.from(gallery.querySelectorAll('gallery-item'));
      gallery.scrollBarElement = gallery.querySelector('.gallery__progress-bar');
      gallery.listWrapperElement = gallery.querySelector('.gallery__list-wrapper');
      if (gallery.listItems.length > 1) {
        gallery.addEventListener('scrollable-content:progress', gallery._updateProgressBar.bind(gallery));
        gallery.addEventListener('prev-next:prev', gallery.previous.bind(gallery));
        gallery.addEventListener('prev-next:next', gallery.next.bind(gallery));
      }

      const showSliderControls = itemsCount > SLIDER_ITEM_THRESHOLD;
      controls?.toggleAttribute('hidden', !showSliderControls);
      progress?.toggleAttribute('hidden', !showSliderControls);
      gallery.scrollBarElement?.style.setProperty('--divider', String(itemsCount));
      if (showSliderControls) {
        this.setupArrowState(gallery.listWrapperElement, controls);
      }
      window.requestAnimationFrame(() => {
        gallery.listWrapperElement?._checkScrollability();
        this.updateArrowState?.();
      });
    }

    // スクロール位置に応じて先頭/末尾で prev/next を disabled 化する（CSS 側で visibility: hidden になる）。
    setupArrowState(scrollableElement, controls) {
      const previousButton = controls?.querySelector('.prev-next-button--prev');
      const nextButton = controls?.querySelector('.prev-next-button--next');
      if (!scrollableElement || !previousButton || !nextButton) {
        return;
      }

      this.updateArrowState = () => {
        const maxScrollLeft = scrollableElement.scrollWidth - scrollableElement.clientWidth;
        const currentScrollLeft = Math.abs(scrollableElement.scrollLeft);
        const isNotScrollable = maxScrollLeft <= SCROLL_EDGE_TOLERANCE_PX;
        previousButton.disabled = isNotScrollable || currentScrollLeft <= SCROLL_EDGE_TOLERANCE_PX;
        nextButton.disabled = isNotScrollable || currentScrollLeft >= maxScrollLeft - SCROLL_EDGE_TOLERANCE_PX;
      };

      scrollableElement.addEventListener('scroll', this.updateArrowState, { passive: true });
      window.addEventListener('resize', this.updateArrowState);
    }

    async handleProductClick(event, productId, spec, cookieId, requestId) {
      const link = event.currentTarget;
      const params = new URLSearchParams();
      params.set('prod', productId);
      params.set('merch', CONFIG.merchant);
      params.set('spec', spec);
      if (cookieId) {
        params.set('cookie', cookieId);
      }
      if (requestId) {
        params.set('cref', requestId);
      }
      params.set('callback', NOOP_CALLBACK_NAME);

      const clickUrl = `${buildBaseUrl('click')}?${params.toString()}`;
      const opensNewContext = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank';
      if (opensNewContext) {
        this.sendClickNotification(clickUrl);
        return;
      }

      event.preventDefault();
      await this.sendClickNotification(clickUrl);
      window.location.assign(link.href);
    }

    async sendClickNotification(clickUrl) {
      try {
        await fetch(clickUrl, { mode: 'no-cors', keepalive: true });
      } catch (error) {
        // 通知失敗時も商品遷移は継続する。
      }
    }
  }

  if (!customElements.get('silveregg-recommend')) {
    customElements.define('silveregg-recommend', SilvereggRecommend);
  }
})();
